import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  Upload, FileSpreadsheet, X, Loader2, ArrowRight, Globe, MapPin, AlertTriangle,
  Zap, Check, DollarSign, BarChart3, PieChart, TrendingDown, ArrowUpRight,
  ArrowDownRight, Target, ExternalLink, Brain
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, ApiError, API_URL } from '../../lib/apiClient';

export default function FinancialTab({ user, setUser, setActiveTab }) {
  const navigate = useNavigate();

  // Financial Impact Analyzer state
  const [financialFile, setFinancialFile] = useState(null);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [financialResults, setFinancialResults] = useState(null);
  const [financialAiBrief, setFinancialAiBrief] = useState(null);
  const [aiBriefLoading, setAiBriefLoading] = useState(false);
  const [classifyLoading, setClassifyLoading] = useState({});
  const [financialPreview, setFinancialPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [progressData, setProgressData] = useState(null); // Progressive results

  const handleFinancialFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.match(/\.(csv|xlsx|xls)$/i)) {
        toast.error('Please upload a CSV or Excel file');
        return;
      }
      setFinancialFile(file);
      setFinancialResults(null);
      setFinancialPreview(null);
      
      // Get preview of the file
      setPreviewLoading(true);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const data = await apiPost('/api/financial/preview', formData);

        if (data?.success) {
          setFinancialPreview(data);
        } else {
          toast.error(data?.error || 'Could not preview file');
          setFinancialFile(null);
        }
      } catch (error) {
        console.error('Preview error:', error);
        const msg = error instanceof ApiError ? (error.message || 'Could not preview file') : 'Could not preview file';
        toast.error(msg);
      } finally {
        setPreviewLoading(false);
      }
    }
  };


  const handleFinancialAnalysis = async () => {
    if (!financialFile) {
      toast.error('Please select a file first');
      return;
    }

    setFinancialLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', financialFile);

      // Start the analysis (upload + queue processing)
      const data = await apiPost('/api/financial/analyze', formData);

      // Got job_id, now poll for status
      const jobId = data.job_id;
      toast.info(`Processing ${data.sku_count} SKUs... This may take a moment.`);
      setProgressData({ processed: 0, total: data.sku_count, progress_percent: 0 });

      // Poll for completion
      let attempts = 0;
      const maxAttempts = 120; // 2 minutes max with 1 second intervals

      const pollStatus = async () => {
        try {
          const statusData = await apiGet(`/api/financial/status/${jobId}`);

          // Update progressive data for live display
          setProgressData({
            processed: statusData.processed || 0,
            total: statusData.total || data.sku_count,
            progress_percent: statusData.progress_percent || 0,
            running_totals: statusData.running_totals || null,
            partial_results: statusData.partial_results || [],
            status: statusData.status
          });

          if (statusData.status === 'complete') {
            // Fetch the full analysis results
            const analysisData = await apiGet(`/api/financial/analysis/${statusData.analysis_id}`);

            setFinancialResults({
              analysis_id: statusData.analysis_id,
              summary: analysisData.summary,
              country_breakdown: analysisData.country_breakdown,
              top_impact_skus: analysisData.results?.slice(0, 10) || [],
              all_results: analysisData.results || []
            });
            
            toast.success(`Analysis complete! ${analysisData.summary?.total_skus_analyzed || 0} SKUs analyzed.`);
            setFinancialLoading(false);
            setProgressData(null);
            
            // Auto-fetch AI executive brief
            fetchFinancialAiBrief(statusData.analysis_id);
            
            return;
          }
          
          if (statusData.status === 'failed') {
            toast.error(statusData.error || 'Analysis failed');
            setFinancialLoading(false);
            setProgressData(null);
            return;
          }
          
          // Still processing - update progress toast
          if (statusData.processed > 0) {
            toast.loading(`Processing... ${statusData.processed}/${statusData.total} SKUs (${statusData.progress_percent}%)`, {
              id: 'financial-progress'
            });
          }
          
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 1000); // Poll every second
          } else {
            toast.error('Analysis timed out. Please check back in Analysis History.');
            setFinancialLoading(false);
            setProgressData(null);
          }
        } catch (error) {
          console.error('Polling error:', error);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 2000); // Retry with longer delay on error
          } else {
            toast.error('Lost connection. Please check Analysis History for results.');
            setFinancialLoading(false);
          }
        }
      };
      
      // Start polling
      pollStatus();

    } catch (error) {
      console.error('Financial analysis error:', error);
      const msg = error instanceof ApiError ? (error.message || 'Analysis failed') : 'Connection error. Please check your file format and try again.';
      toast.error(msg);
      setFinancialLoading(false);
    }
  };


  const handleClassifySku = async (analysisId, skuId) => {
    setClassifyLoading(prev => ({ ...prev, [skuId]: true }));
    try {
      const data = await apiPost(`/api/financial/classify-sku/${analysisId}/${skuId}`);
      toast.success('Classification complete!');
      navigate(`/analysis/${data.analysis_id}`);
    } catch (error) {
      const msg = error instanceof ApiError ? (error.message || 'Classification failed') : 'Connection error. Please try again.';
      toast.error(msg);
    } finally {
      setClassifyLoading(prev => ({ ...prev, [skuId]: false }));
    }
  };

  const clearFinancialFile = () => {
    setFinancialFile(null);
    setFinancialResults(null);
    setFinancialPreview(null);
    setFinancialAiBrief(null);
  };


  const fetchFinancialAiBrief = async (analysisId) => {
    setAiBriefLoading(true);
    try {
      const data = await apiPost(`/api/financial/ai-brief/${analysisId}`);
      if (data) setFinancialAiBrief(data);
    } catch (error) {
      console.error('AI Brief fetch error:', error);
    } finally {
      setAiBriefLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const isFreeTier = user?.subscription_tier === 'free';


  return (
          /* Financial Impact Analyzer - Refined Theme */
          <div className="space-y-6 -mx-6 lg:-mx-8 -mt-2">

            {/* ===== HERO HEADER ===== */}
            <div className="bg-gradient-to-br from-[#059669] to-[#047857] rounded-none lg:rounded-2xl px-6 lg:px-10 py-8 relative overflow-hidden">
              {/* Background accents */}
              <div className="absolute top-0 right-0 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-1/3 w-48 h-48 bg-white/5 rounded-full blur-2xl" />
              
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-white" />
                    </div>
                    <div className="h-6 w-px bg-white/20" />
                    <span className="text-xs font-semibold tracking-widest text-emerald-100 uppercase">Financial Intelligence</span>
                  </div>
                  <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white mb-1">Financial Impact Analyzer</h1>
                  <p className="text-emerald-100/80 text-sm font-jakarta max-w-lg">
                    See exactly how much you're overpaying — or underreporting — in duties
                  </p>
                </div>
                {financialResults && (
                  <Button
                    onClick={clearFinancialFile}
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10"
                    data-testid="new-financial-analysis-btn"
                  >
                    <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                    New Analysis
                  </Button>
                )}
              </div>
            </div>

            {/* ===== UPLOAD SECTION ===== */}
            {!financialResults && (
              <div className="px-6 lg:px-8 space-y-5">
                <div className="fin-card p-6 lg:p-8">
                  {/* Required columns - compact horizontal */}
                  <div className="flex items-center gap-3 mb-6 flex-wrap">
                    <span className="text-xs font-semibold tracking-wider text-slate-400 uppercase">Required:</span>
                    {['SKU / Product ID', 'Quantity', 'Customs Value', 'Country of Origin', 'HS Code'].map((col) => (
                      <span key={col} className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-50 text-slate-600 border border-slate-200">{col}</span>
                    ))}
                    <span className="text-xs text-slate-400 ml-1">Optional: Duty Paid, Import Country, Description</span>
                  </div>

                  {/* Upload Zone */}
                  <div className={`fin-upload-zone p-10 text-center ${financialFile ? 'active' : ''}`}>
                    {financialFile ? (
                      <div className="flex items-center justify-center gap-5">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#059669] to-[#10B981] flex items-center justify-center shadow-lg shadow-emerald-500/20">
                          <FileSpreadsheet className="w-7 h-7 text-white" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-slate-900 text-base">{financialFile.name}</p>
                          <p className="text-sm text-slate-500">{(financialFile.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <button 
                          onClick={clearFinancialFile}
                          className="ml-4 p-2.5 hover:bg-slate-100 rounded-xl transition-colors"
                          data-testid="clear-financial-file-btn"
                        >
                          <X className="w-5 h-5 text-slate-400 hover:text-slate-600" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="w-16 h-16 rounded-2xl bg-[#059669]/10 border border-[#059669]/20 flex items-center justify-center mx-auto mb-4">
                          <Upload className="w-7 h-7 text-[#059669]" />
                        </div>
                        <p className="text-slate-600 mb-1.5">
                          <label className="text-[#059669] font-semibold cursor-pointer hover:text-[#047857] transition-colors">
                            Upload Transaction Data
                            <input
                              type="file"
                              className="hidden"
                              accept=".csv,.xlsx,.xls"
                              onChange={handleFinancialFileChange}
                              data-testid="financial-file-input"
                            />
                          </label>
                        </p>
                        <p className="text-xs text-slate-400">CSV, Excel files supported</p>
                      </>
                    )}
                  </div>

                  {/* Loading preview */}
                  {previewLoading && (
                    <div className="mt-5 p-5 fin-card-glass flex items-center justify-center gap-3">
                      <Loader2 className="w-5 h-5 animate-spin text-[#059669]" />
                      <span className="text-sm text-slate-500">Analyzing file structure...</span>
                    </div>
                  )}

                  {/* Pre-Upload Portfolio Snapshot */}
                  {financialPreview && !previewLoading && (
                    <div className="mt-5 p-6 bg-gradient-to-br from-[#059669]/5 to-[#10B981]/5 rounded-xl border border-[#059669]/15" data-testid="financial-preview">
                      <div className="flex items-center gap-2.5 mb-5">
                        <div className="w-8 h-8 rounded-lg bg-[#059669]/15 flex items-center justify-center">
                          <BarChart3 className="w-4 h-4 text-[#059669]" />
                        </div>
                        <h4 className="font-semibold text-slate-900 font-outfit">Portfolio Snapshot</h4>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                        <div className="bg-white rounded-xl p-4 text-center border border-slate-100 shadow-sm">
                          <p className="text-2xl font-bold text-slate-900 font-outfit">{financialPreview.preview.total_rows.toLocaleString()}</p>
                          <p className="text-xs text-slate-500 mt-0.5">Transactions</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 text-center border border-slate-100 shadow-sm">
                          <p className="text-2xl font-bold text-[#059669] font-outfit">{financialPreview.preview.unique_skus}</p>
                          <p className="text-xs text-slate-500 mt-0.5">Unique SKUs</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 text-center border border-slate-100 shadow-sm">
                          <p className="text-2xl font-bold text-slate-900 font-outfit">
                            {financialPreview.preview.total_customs_value ? `$${(financialPreview.preview.total_customs_value / 1000000 >= 1 ? (financialPreview.preview.total_customs_value / 1000000).toFixed(1) + 'M' : (financialPreview.preview.total_customs_value / 1000).toFixed(0) + 'K')}` : 'N/A'}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">Customs Value</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 text-center border border-slate-100 shadow-sm">
                          <p className="text-2xl font-bold text-amber-600 font-outfit">{financialPreview.preview.estimated_processing_time}</p>
                          <p className="text-xs text-slate-500 mt-0.5">Est. Processing</p>
                        </div>
                      </div>

                      {/* Data quality signals */}
                      <div className="space-y-2 mb-4">
                        {financialPreview.preview.concentration_risk != null && (
                          <div className={`flex items-center gap-2 p-2.5 rounded-lg text-sm ${financialPreview.preview.concentration_risk > 60 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
                            {financialPreview.preview.concentration_risk > 60 ? <AlertTriangle className="w-4 h-4 flex-shrink-0" /> : <Check className="w-4 h-4 flex-shrink-0" />}
                            <span>{financialPreview.preview.concentration_risk}% of value in top 3 SKUs — {financialPreview.preview.concentration_risk > 60 ? 'high concentration risk' : 'well diversified'}</span>
                          </div>
                        )}
                        {financialPreview.preview.hs_quality && (financialPreview.preview.hs_quality.short_4_digit > 0 || financialPreview.preview.hs_quality.missing > 0) && (
                          <div className="flex items-center gap-2 p-2.5 rounded-lg text-sm bg-amber-50 text-amber-700">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                            <span>
                              {financialPreview.preview.hs_quality.missing > 0 && `${financialPreview.preview.hs_quality.missing} rows missing HS codes`}
                              {financialPreview.preview.hs_quality.missing > 0 && financialPreview.preview.hs_quality.short_4_digit > 0 && ', '}
                              {financialPreview.preview.hs_quality.short_4_digit > 0 && `${financialPreview.preview.hs_quality.short_4_digit} with only 4-digit codes`}
                              {' — full AI classification applied'}
                            </span>
                          </div>
                        )}
                        {financialPreview.preview.deduplication_savings > 0 && (
                          <div className="flex items-center gap-2 p-2.5 rounded-lg text-sm bg-[#059669]/10 text-[#059669]">
                            <Zap className="w-4 h-4 flex-shrink-0" />
                            <span>{financialPreview.preview.deduplication_savings} duplicates combined ({financialPreview.preview.deduplication_percent}% reduction)</span>
                          </div>
                        )}
                      </div>

                      {/* Country Mix */}
                      {financialPreview.preview.country_mix && Object.keys(financialPreview.preview.country_mix).length > 0 && (
                        <div className="border-t border-[#059669]/10 pt-3">
                          <p className="text-xs font-semibold tracking-wider text-slate-400 mb-2 uppercase">Country Mix</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(financialPreview.preview.country_mix).map(([country, data]) => (
                              <span key={country} className="text-xs bg-white px-2.5 py-1.5 rounded-lg border border-slate-200">
                                <span className="font-semibold text-slate-700">{country}</span>
                                <span className="text-slate-400 ml-1">
                                  {data.percent}% {data.value > 0 ? `($${(data.value/1000).toFixed(0)}K)` : ''}
                                </span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {!financialPreview.usage.can_process && (
                        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <p className="text-sm text-amber-700">
                            Exceeds {financialPreview.usage.tier} limit ({financialPreview.usage.current_usage}/{financialPreview.usage.tier_limit} used).
                            <a href="/billing" className="ml-1 underline font-medium">Upgrade</a>
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* CTA Button */}
                  <button
                    onClick={handleFinancialAnalysis}
                    className={`w-full mt-6 py-4 rounded-xl font-semibold text-base flex items-center justify-center gap-2 transition-all duration-300 fin-btn-shimmer ${
                      !financialFile || financialLoading || previewLoading || (financialPreview && !financialPreview.usage.can_process)
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-[#059669] to-[#10B981] text-white hover:from-[#047857] hover:to-[#059669] shadow-lg shadow-[#059669]/20 hover:shadow-[#059669]/30'
                    }`}
                    disabled={!financialFile || financialLoading || previewLoading || (financialPreview && !financialPreview.usage.can_process)}
                    data-testid="analyze-financial-btn"
                  >
                    {financialLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Analyzing Transactions...
                      </>
                    ) : (
                      <>
                        <BarChart3 className="w-5 h-5" />
                        Run Financial Analysis
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-slate-400 mt-3">AI classifies each SKU individually — large files may take 30+ seconds</p>
                </div>
              </div>
            )}

            {/* ===== PROGRESSIVE RESULTS PANEL ===== */}
            {financialLoading && progressData && (
              <div className="px-6 lg:px-8 space-y-5" data-testid="financial-progressive-panel">
                {/* Progress Bar */}
                <div className="fin-card p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 animate-spin text-[#059669]" />
                      <span className="font-semibold text-slate-900 font-outfit">Analyzing Transactions</span>
                    </div>
                    <span className="text-sm font-bold text-[#059669]">{progressData.processed}/{progressData.total} SKUs</span>
                  </div>
                  <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#059669] to-[#10B981] rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${progressData.progress_percent}%` }} />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Results appear as each product is classified...</p>
                </div>

                {/* Running Totals — live counters */}
                {progressData.running_totals && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="fin-card p-4 text-center">
                      <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Overpayment Found</p>
                      <p className="text-xl font-bold text-red-600 font-outfit">{formatCurrency(progressData.running_totals.overpayment || 0)}</p>
                    </div>
                    <div className="fin-card p-4 text-center">
                      <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Underpayment Risk</p>
                      <p className="text-xl font-bold text-amber-600 font-outfit">{formatCurrency(progressData.running_totals.underpayment_risk || 0)}</p>
                    </div>
                    <div className="fin-card p-4 text-center">
                      <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Annual Exposure</p>
                      <p className="text-xl font-bold text-blue-600 font-outfit">{formatCurrency(progressData.running_totals.exposure || 0)}</p>
                    </div>
                    <div className="fin-card p-4 text-center">
                      <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Value Processed</p>
                      <p className="text-xl font-bold text-slate-700 font-outfit">{formatCurrency(progressData.running_totals.processed_value || 0)}</p>
                    </div>
                  </div>
                )}

                {/* Partial Results — top findings so far */}
                {progressData.partial_results?.length > 0 && (
                  <div className="fin-card overflow-hidden">
                    <div className="px-5 py-3 bg-slate-50 border-b flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-red-500" />
                      <span className="font-semibold text-sm text-slate-700">Top Findings So Far</span>
                      <span className="text-xs text-slate-400 ml-auto">{progressData.partial_results.length} high-impact items</span>
                    </div>
                    <div className="divide-y divide-slate-50">
                      {progressData.partial_results.slice(0, 5).map((r, i) => (
                        <div key={i} className="px-5 py-3 flex items-center gap-4 hover:bg-slate-50/50">
                          <span className="w-6 h-6 rounded-full bg-red-50 text-red-600 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{r.sku_id || r.description || `SKU ${i + 1}`}</p>
                            <p className="text-xs text-slate-500">{r.current_hs_code} → {r.suggested_hs_code}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-red-600">{formatCurrency(r.annual_exposure || 0)}</p>
                            <p className="text-[10px] text-slate-400">annual exposure</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ===== RESULTS SECTION ===== */}
            {financialResults && (
              <div className="px-6 lg:px-8 space-y-5">

                {/* Executive Summary Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="fin-card fin-stat-card fin-stat-emerald p-5 fin-animate fin-delay-1">
                    <p className="text-xs font-medium text-slate-500 mb-1.5 tracking-wide uppercase">Total Customs Value</p>
                    <p className="text-2xl font-bold text-slate-900 font-outfit">{formatCurrency(financialResults.summary.total_customs_value)}</p>
                    <p className="text-xs text-slate-400 mt-1.5">{financialResults.summary.total_transactions} transactions</p>
                  </div>
                  <div className="fin-card fin-stat-card fin-stat-red p-5 fin-animate fin-delay-2">
                    <p className="text-xs font-medium text-slate-500 mb-1.5 tracking-wide uppercase">Overpayment Exposure</p>
                    <p className="text-2xl font-bold text-red-600 font-outfit">{formatCurrency(financialResults.summary.total_overpayment_exposure)}</p>
                    <div className="flex items-center gap-1 mt-1.5">
                      <TrendingDown className="w-3 h-3 text-red-500" />
                      <p className="text-xs text-red-500">Potential recovery</p>
                    </div>
                  </div>
                  <div className="fin-card fin-stat-card fin-stat-amber p-5 fin-animate fin-delay-3">
                    <p className="text-xs font-medium text-slate-500 mb-1.5 tracking-wide uppercase">Underpayment Risk</p>
                    <p className="text-2xl font-bold text-amber-600 font-outfit">{formatCurrency(financialResults.summary.total_underpayment_risk)}</p>
                    <div className="flex items-center gap-1 mt-1.5">
                      <AlertTriangle className="w-3 h-3 text-amber-500" />
                      <p className="text-xs text-amber-500">Compliance risk</p>
                    </div>
                  </div>
                  <div className="fin-card fin-stat-card fin-stat-blue p-5 fin-animate fin-delay-4">
                    <p className="text-xs font-medium text-slate-500 mb-1.5 tracking-wide uppercase">Annual Exposure</p>
                    <p className="text-2xl font-bold text-blue-600 font-outfit">{formatCurrency(financialResults.summary.total_annual_exposure)}</p>
                    <p className="text-xs text-slate-400 mt-1.5">{financialResults.summary.high_priority_count} high priority</p>
                  </div>
                </div>

                {/* Accuracy + Country Row */}
                <div className="grid lg:grid-cols-2 gap-5">
                  {/* Classification Accuracy */}
                  <div className="fin-card p-6 fin-animate fin-delay-3">
                    <h3 className="font-outfit font-semibold text-slate-900 mb-5 flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-[#059669]" />
                      Classification Accuracy
                    </h3>
                    <div className="flex items-center gap-8">
                      <div className="relative w-36 h-36 fin-ring-glow">
                        <svg className="w-36 h-36 transform -rotate-90">
                          <circle cx="72" cy="72" r="60" fill="none" stroke="#E2E8F0" strokeWidth="10" />
                          <circle 
                            cx="72" cy="72" r="60" fill="none" stroke="#059669" strokeWidth="10"
                            strokeDasharray={`${(100 - financialResults.summary.misclassification_rate) * 3.77} 377`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-3xl font-bold text-slate-900 font-outfit">{(100 - financialResults.summary.misclassification_rate).toFixed(0)}%</span>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-[#059669]"></div>
                          <span className="text-sm text-slate-600">Correctly classified</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-slate-200"></div>
                          <span className="text-sm text-slate-500">Misclassified ({financialResults.summary.misclassified_sku_count} SKUs)</span>
                        </div>
                        <p className="text-xs text-slate-400 pt-1">
                          Avg confidence: {(financialResults.summary.average_confidence * 100).toFixed(0)}%
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Country Exposure */}
                  <div className="fin-card p-6 fin-animate fin-delay-4">
                    {financialAiBrief?.country_narrative && (
                      <div className="mb-4 p-3 bg-[#6366F1]/5 rounded-lg border border-[#6366F1]/15">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Brain className="w-3.5 h-3.5 text-[#6366F1]" />
                          <span className="text-xs font-semibold text-[#6366F1]">AI Insight</span>
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">{financialAiBrief.country_narrative}</p>
                      </div>
                    )}
                    <h3 className="font-outfit font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Globe className="w-5 h-5 text-[#059669]" />
                      Exposure by Origin
                    </h3>
                    <div className="space-y-2.5 max-h-[180px] overflow-y-auto pr-1">
                      {Object.entries(financialResults.country_breakdown)
                        .sort((a, b) => (b[1].overpayment + b[1].underpayment_risk) - (a[1].overpayment + a[1].underpayment_risk))
                        .slice(0, 5)
                        .map(([country, data]) => {
                          const total = data.overpayment + data.underpayment_risk;
                          const maxTotal = Math.max(...Object.values(financialResults.country_breakdown).map(d => d.overpayment + d.underpayment_risk));
                          const barWidth = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
                          return (
                            <div key={country} className="group">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                                  <span className="text-sm font-medium text-slate-700">{country}</span>
                                </div>
                                <div className="text-right">
                                  <span className="text-sm font-semibold text-slate-900">{formatCurrency(total)}</span>
                                  <span className="text-xs text-slate-400 ml-2">{data.transaction_count} txns</span>
                                </div>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-[#059669] to-[#10B981] rounded-full transition-all duration-700" style={{ width: `${barWidth}%` }} />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>

                {/* AI Executive Brief */}
                {(financialAiBrief || aiBriefLoading) && (
                  <div className="fin-card fin-ai-card p-6 border-l-4 border-l-[#6366F1] fin-animate fin-delay-5" data-testid="ai-executive-brief">
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-[#6366F1]/10 flex items-center justify-center">
                        <Brain className="w-4 h-4 text-[#6366F1]" />
                      </div>
                      <h3 className="font-outfit font-semibold text-slate-900">AI Executive Brief</h3>
                      {aiBriefLoading && <Loader2 className="w-4 h-4 animate-spin text-[#6366F1]" />}
                    </div>
                    {aiBriefLoading ? (
                      <div className="space-y-2.5">
                        <div className="h-4 bg-slate-100 rounded animate-pulse w-full" />
                        <div className="h-4 bg-slate-100 rounded animate-pulse w-[90%]" />
                        <div className="h-4 bg-slate-100 rounded animate-pulse w-[95%]" />
                      </div>
                    ) : financialAiBrief?.executive_brief ? (
                      <p className="text-sm text-slate-700 leading-relaxed">{financialAiBrief.executive_brief}</p>
                    ) : null}
                  </div>
                )}

                {/* Penalty & Risk Estimator */}
                {financialAiBrief?.penalty_estimates?.length > 0 && (
                  <div className="fin-card p-6 border-l-4 border-l-red-500 fin-animate fin-delay-5" data-testid="penalty-estimator">
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                        </div>
                        <h3 className="font-outfit font-semibold text-slate-900">Penalty & Risk Estimator</h3>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Worst-Case Exposure</p>
                        <p className="text-xl font-bold text-red-600 font-outfit fin-pulse-critical">{formatCurrency(financialAiBrief.total_worst_case_exposure)}</p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2.5 px-3 text-xs text-slate-500 font-medium tracking-wide uppercase">SKU</th>
                            <th className="text-right py-2.5 px-3 text-xs text-slate-500 font-medium tracking-wide uppercase">Annual Under.</th>
                            <th className="text-right py-2.5 px-3 text-xs text-slate-500 font-medium tracking-wide uppercase">5yr Back-Duties</th>
                            <th className="text-right py-2.5 px-3 text-xs text-slate-500 font-medium tracking-wide uppercase">Penalty Range</th>
                            <th className="text-right py-2.5 px-3 text-xs text-slate-500 font-medium tracking-wide uppercase">Worst Case</th>
                          </tr>
                        </thead>
                        <tbody>
                          {financialAiBrief.penalty_estimates.slice(0, 5).map((pe, idx) => (
                            <tr key={idx} className="fin-table-row border-b border-slate-100">
                              <td className="py-3 px-3">
                                <span className="font-medium text-slate-700">{pe.sku_id}</span>
                                <p className="text-xs text-slate-400 truncate max-w-[200px]">{pe.description}</p>
                              </td>
                              <td className="py-3 px-3 text-right font-medium text-amber-600">{formatCurrency(pe.annual_underpayment)}</td>
                              <td className="py-3 px-3 text-right text-slate-600">{formatCurrency(pe.back_duties_5yr)}</td>
                              <td className="py-3 px-3 text-right text-slate-600">{formatCurrency(pe.penalty_range_low)} – {formatCurrency(pe.penalty_range_high)}</td>
                              <td className="py-3 px-3 text-right font-bold text-red-600">{formatCurrency(pe.worst_case_total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-slate-400 mt-3">Based on CBP penalty guidelines: 20-400% of unpaid duties + 5yr lookback + interest.</p>
                  </div>
                )}

                {/* Top Actions by Dollar Impact */}
                {financialAiBrief?.top_actions?.length > 0 && (
                  <div className="fin-card p-6 fin-animate fin-delay-6" data-testid="top-actions">
                    <div className="flex items-center gap-2.5 mb-5">
                      <div className="w-8 h-8 rounded-lg bg-[#6366F1]/10 flex items-center justify-center">
                        <Target className="w-4 h-4 text-[#6366F1]" />
                      </div>
                      <h3 className="font-outfit font-semibold text-slate-900">Top Actions by Dollar Impact</h3>
                    </div>
                    <div className="space-y-3">
                      {financialAiBrief.top_actions.map((action, idx) => (
                        <div key={idx} className={`flex items-center justify-between p-4 rounded-xl border transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                          action.type === 'underpayment' 
                            ? 'bg-red-50/50 border-red-200 hover:border-red-300 hover:shadow-red-100' 
                            : 'bg-green-50/50 border-green-200 hover:border-green-300 hover:shadow-green-100'
                        }`} data-testid={`top-action-${idx}`}>
                          <div className="flex items-center gap-3">
                            <span className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold text-white ${
                              action.type === 'underpayment' ? 'bg-red-500' : 'bg-[#10B981]'
                            }`}>{idx + 1}</span>
                            <div>
                              <p className="text-sm font-medium text-slate-700">{action.action}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                action.type === 'underpayment' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                              }`}>{action.type === 'underpayment' ? 'Compliance Risk' : 'Recovery Opportunity'}</span>
                            </div>
                          </div>
                          <span className={`text-lg font-bold whitespace-nowrap font-outfit ${
                            action.type === 'underpayment' ? 'text-red-600' : 'text-[#059669]'
                          }`}>{action.impact_label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Impact SKUs Table */}
                <div className="fin-card p-6 fin-animate fin-delay-6">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-outfit font-semibold text-slate-900 flex items-center gap-2">
                      <Target className="w-5 h-5 text-[#059669]" />
                      SKU Impact Analysis
                    </h3>
                    <span className="text-sm text-slate-500">{financialResults.all_results.length} SKUs analyzed</span>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-3 text-xs font-medium text-slate-500 tracking-wide uppercase">SKU</th>
                          <th className="text-left py-3 px-3 text-xs font-medium text-slate-500 tracking-wide uppercase">Declared HS</th>
                          <th className="text-left py-3 px-3 text-xs font-medium text-slate-500 tracking-wide uppercase">Suggested HS</th>
                          <th className="text-right py-3 px-3 text-xs font-medium text-slate-500 tracking-wide uppercase">Rate Delta</th>
                          <th className="text-right py-3 px-3 text-xs font-medium text-slate-500 tracking-wide uppercase">Exposure</th>
                          <th className="text-center py-3 px-3 text-xs font-medium text-slate-500 tracking-wide uppercase">Priority</th>
                          <th className="text-center py-3 px-3 text-xs font-medium text-slate-500 tracking-wide uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financialResults.all_results.slice(0, 15).map((sku, index) => (
                          <tr key={index} className="fin-table-row border-b border-slate-100">
                            <td className="py-3 px-3">
                              <p className="font-medium text-slate-900">{sku.sku_id}</p>
                              <p className="text-xs text-slate-400 truncate max-w-[150px]">{sku.description}</p>
                            </td>
                            <td className="py-3 px-3">
                              <span className={`font-mono text-sm ${!sku.classification_match ? 'text-red-600' : 'text-slate-600'}`}>
                                {sku.declared_hs_code}
                              </span>
                              <p className="text-xs text-slate-400">{sku.declared_duty_rate}%</p>
                            </td>
                            <td className="py-3 px-3">
                              <span className="font-mono text-sm text-[#059669]">{sku.suggested_hs_code}</span>
                              <p className="text-xs text-slate-400">{sku.suggested_duty_rate}%</p>
                            </td>
                            <td className="py-3 px-3 text-right">
                              <span className={`font-semibold ${
                                sku.duty_rate_delta > 0 ? 'text-red-600' : 
                                sku.duty_rate_delta < 0 ? 'text-[#059669]' : 'text-slate-400'
                              }`}>
                                {sku.duty_rate_delta > 0 ? '+' : ''}{sku.duty_rate_delta.toFixed(1)}%
                              </span>
                            </td>
                            <td className="py-3 px-3 text-right">
                              <p className="font-semibold text-slate-900">{formatCurrency(sku.annual_exposure)}</p>
                              {sku.overpayment_amount > 0 && (
                                <p className="text-xs text-red-500 flex items-center justify-end gap-1">
                                  <ArrowDownRight className="w-3 h-3" />
                                  Over: {formatCurrency(sku.overpayment_amount)}
                                </p>
                              )}
                              {sku.underpayment_risk > 0 && (
                                <p className="text-xs text-amber-500 flex items-center justify-end gap-1">
                                  <ArrowUpRight className="w-3 h-3" />
                                  Under: {formatCurrency(sku.underpayment_risk)}
                                </p>
                              )}
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                                sku.priority_level === 'HIGH' ? 'bg-red-100 text-red-700' :
                                sku.priority_level === 'MEDIUM' ? 'bg-amber-100 text-amber-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {sku.priority_level}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleClassifySku(financialResults.analysis_id, sku.sku_id)}
                                disabled={classifyLoading[sku.sku_id]}
                                className="text-xs"
                                data-testid={`classify-sku-${sku.sku_id}`}
                              >
                                {classifyLoading[sku.sku_id] ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <>
                                    <ExternalLink className="w-3 h-3 mr-1" />
                                    Classify
                                  </>
                                )}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {financialResults.all_results.length > 15 && (
                    <p className="text-center text-sm text-slate-500 mt-4">
                      Showing top 15 of {financialResults.all_results.length} SKUs. Export for full data.
                    </p>
                  )}
                </div>

                {/* Export Button */}
                <div className="flex justify-center pb-4">
                  <a
                    href={`${API_URL}/api/financial/export/${financialResults.analysis_id}?token=${localStorage.getItem('decoded_token')}`}
                    className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-gradient-to-r from-[#059669] to-[#10B981] text-white rounded-xl font-semibold hover:from-[#047857] hover:to-[#059669] transition-all shadow-lg shadow-[#059669]/15 hover:shadow-[#059669]/25 hover:-translate-y-0.5"
                    data-testid="export-financial-csv"
                  >
                    <FileSpreadsheet className="w-5 h-5" />
                    Export Full Analysis (CSV)
                  </a>
                </div>
              </div>
            )}
          </div>
  );
}
